import React, { useState, useEffect } from 'react';
import { Link,useLocation } from 'react-router-dom';
import { MapPin, Filter } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useContext } from 'react';
import { ProjectsContext } from '../admin/ProjectsContext';


const Projects = () => {
  const { projects } = useContext(ProjectsContext);
  const [filteredProjects, setFilteredProjects] = useState(projects);
  const [filters, setFilters] = useState({
    type: 'all',
    approval: 'all',
    status: 'all'
  });
const location = useLocation();
useEffect(() => {
  const params = new URLSearchParams(location.search);
  const typeFromUrl = params.get("type");

  if (typeFromUrl) {
    setFilters((prev) => ({
      ...prev,
      type: typeFromUrl,
    }));
  }
}, [location.search]);

  useEffect(() => {
    let filtered = projects;

    if (filters.type !== 'all') {
      filtered = filtered.filter(p => p.category === filters.type);
    }
    if (filters.approval !== 'all') {
      filtered = filtered.filter(p => p.approvals.includes(filters.approval));
    }
    if (filters.status !== 'all') {
      filtered = filtered.filter(p => p.status === filters.status);
    }

    setFilteredProjects(filtered);
  }, [filters, projects]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, index) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('revealed');
            }, index * 100);
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.scroll-reveal').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [filteredProjects]);

const projectTypes = [...new Set(projects.map(p => p.category))];
  const projectStatuses = [...new Set(projects.map(p => p.status))];

  return (
    <div className="min-h-screen py-20" style={{ backgroundColor: 'var(--bg-1)' }}>
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-brand-dark mb-4">
            Our Projects
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Discover our portfolio of VMRDA & RERA approved properties across Visakhapatnam. From residential plots to luxury villas and modern apartments.
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={20} className="text-brand-gold" />
            <h3 className="font-bold text-lg text-brand-dark">Filter Projects</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Property Type</label>
              <Select
                value={filters.type}
                onValueChange={(value) => setFilters({ ...filters, type: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
  <SelectItem value="all">All</SelectItem>
  <SelectItem value="residential">Residential Projects</SelectItem>
  <SelectItem value="farmland">Farmland Projects</SelectItem>
</SelectContent>

              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Approval Status</label>
              <Select
                value={filters.approval}
                onValueChange={(value) => setFilters({ ...filters, approval: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Approvals" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Approvals</SelectItem>
                  <SelectItem value="VMRDA Approved">VMRDA Approved</SelectItem>
                  <SelectItem value="RERA Approved">RERA Approved</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Project Status</label>
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters({ ...filters, status: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {projectStatuses.map((status) => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {(filters.type !== 'all' || filters.approval !== 'all' || filters.status !== 'all') && (
            <Button
              onClick={() => setFilters({ type: 'all', approval: 'all', status: 'all' })}
              variant="outline"
              className="mt-4"
            >
              Clear All Filters
            </Button>
          )}
        </div>

        {/* Results Count */}
        <p className="text-gray-600 mb-6">
          Showing <span className="font-bold text-brand-dark">{filteredProjects.length}</span> project{filteredProjects.length !== 1 ? 's' : ''}
        </p>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project, index) => (
            <Link
              key={project.id}
              to={`/projects/${project.id}`}
              className="scroll-reveal"
            >
              <Card className="card-hover overflow-hidden h-full border-2 border-gray-200">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={project.images[0]}
                    alt={project.name}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                  <div className="absolute top-3 right-3 bg-brand-gold text-white px-3 py-1 rounded-full text-xs font-semibold">
                    {project.status}
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-brand-dark mb-2">{project.name}</h3>
                  <p className="text-sm text-gray-600 mb-4">{project.tagline}</p>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                    <MapPin size={16} className="text-brand-gold flex-shrink-0" />
                    <span className="line-clamp-2">{project.location}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-4">
  {project.approvals
    .filter((approval) => approval !== "Clear Title")
    .map((approval, idx) => (
      <span
        key={idx}
        className="text-xs bg-green-50 text-green-700 px-3 py-1 rounded-full font-medium"
      >
        {approval}
      </span>
    ))}
</div>

                  <div className="border-t pt-4 mt-4">
                    <p className="text-brand-gold font-bold text-lg mb-2">{project.priceRange}</p>
                    <Button className="w-full btn-primary">View Details</Button>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {filteredProjects.length === 0 && (
          <div className="text-center py-20">
            <h3 className="text-2xl font-bold text-gray-400 mb-2">No projects found</h3>
            <p className="text-gray-500 mb-6">Try adjusting your filters</p>
            <Button
              onClick={() => setFilters({ type: 'all', approval: 'all', status: 'all' })}
              className="btn-primary"
            >
              Clear Filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Projects;
